"use client"

import { ResizeObserver } from "@juggle/resize-observer"
import clsx from "clsx"
import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
import React, { useContext, useState } from "react"
import useDimensions from "react-cool-dimensions"

import CaratIcon from "../../../../../public/icons/arrow-right.svg"
import Button from "../../Button"
import PageThemeContext from "../../page-theme/PageThemeContext"
import PageGrid from "../../PageGrid"
// import Button from "../../Button"
import { NavItem } from "../types/navigation"

import MenuMobile from "./MenuMobile"

interface INavMobileProps {
  navOpen: boolean
  navData: NavItem[]
  // This shouldnt change but we can monitor for changes using react-cool-dimensions if necessary
  headerHeight: number
  activeMenuName: string | undefined
  setActiveMenuName: React.Dispatch<React.SetStateAction<string | undefined>>
}

const NavMobile: React.FC<INavMobileProps> = ({
  navOpen,
  navData,
  headerHeight,
  activeMenuName,
  setActiveMenuName,
}) => {
  const pageThemeContext = useContext(PageThemeContext)
  const navItemColorClass = pageThemeContext.pageTheme.dark
    ? "text-gray-2.5"
    : "text-gray-90"
  const [navItems] = useState(navData)
  const [activeMenuOpen, setActiveMenuOpen] = useState(false)

  // calculate the height of the bottom bar that has the Contact Sales button
  // use this to calculate the open menu height so that long menu content doesn't overflow the button
  // instead overflow is scrollable while Contact Sales button will remain at bottom of screen
  const { observe: buttonBar, height: buttonBarHeight } = useDimensions({
    useBorderBoxSize: true,
    polyfill: ResizeObserver,
  })

  // Note the use of display block and hidden in the animation.
  // As per framer-motion docs animating to non animatable values like block and hidden sets the values instantly.
  // The upshot of this is that when the menu is open the setting of display block is instantaneous
  // and then the opacity fade happens.
  // When closed the setting of display none happens at the end of the animation, after the opacity fades out.
  // This is what the transitionEnd property does.
  const openMenuVariants: Variants = {
    open: {
      paddingTop: headerHeight,
      opacity: 1,
      display: "block",
      transition: {
        duration: 0.2,
        when: "beforeChildren",
      },
    },
    closed: {
      paddingTop: 0,
      opacity: 0,
      transition: {
        duration: 0.2,
        when: "afterChildren",
      },
      transitionEnd: {
        display: "none",
      },
    },
  }

  const slideMenuVariants: Variants = {
    open: {
      y: "0%",
      height: `calc(100% - ${buttonBarHeight}px)`,
      opacity: 1,
      transition: {
        duration: 0.2,
        staggerChildren: 0.1,
        when: "beforeChildren",
        type: "spring",
        bounce: 0,
      },
    },
    closed: {
      y: "-100%",
      opacity: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
        staggerDirection: -1,
        when: "afterChildren",
        type: "spring",
        bounce: 0,
      },
    },
  }

  const showHideVariants: Variants = {
    open: {
      opacity: 1,
    },
    closed: {
      opacity: 0,
    },
  }

  return (
    <motion.div
      variants={openMenuVariants}
      animate={navOpen ? "open" : "closed"}
      initial="closed"
      className={clsx(
        "absolute top-0 bottom-0 left-0 z-40 w-full h-screen",
        pageThemeContext.pageTheme.bgColorClass,
      )}
    >
      {/* start menu component which slides in */}
      <motion.div
        variants={slideMenuVariants}
        className={clsx(
          "relative top-0 w-full overflow-x-hidden overflow-y-auto",
          pageThemeContext.pageTheme.bgColorClass,
        )}
      >
        <PageGrid isGrid={false} className="md:mt-2">
          <nav className="flex flex-col pl-4">
            {navItems.map((navItem) => (
              <motion.div
                variants={showHideVariants}
                className={clsx(
                  "flex items-center text-base py-4 cursor-pointer",
                  navItemColorClass,
                )}
                onClick={() => {
                  setActiveMenuOpen(!activeMenuOpen)
                  setActiveMenuName(navItem.name)
                }}
                key={navItem.name}
              >
                {navItem.name}
                <CaratIcon className="ml-2" />
              </motion.div>
            ))}
          </nav>
        </PageGrid>
        <MenuMobile
          activeMenuName={activeMenuName}
          activeMenuOpen={activeMenuOpen}
          setActiveMenuOpen={setActiveMenuOpen}
          menuData={navItems}
        />
      </motion.div>
      {/* end menu component which slides in */}
      <motion.div
        className={clsx(
          "absolute bottom-0 left-0 right-0",
          pageThemeContext.pageTheme.bgColorClass,
        )}
        variants={showHideVariants}
        ref={buttonBar}
      >
        <PageGrid
          isGrid={false}
          className={clsx(
            "py-8 pl-4 border-t-2 md:pt-8 md:pb-6",
            pageThemeContext.pageTheme.dark
              ? "border-gray-80"
              : "border-gray-15",
          )}
        >
          {/* this should be Ripple Button size="md" */}
          <Button to="/contact/sales">Contact Sales</Button>
        </PageGrid>
      </motion.div>
    </motion.div>
  )
}

export default NavMobile
